import {type ColorName, colorNames, isColorName, useNamedColor} from '@github-ui/use-named-color'
import {CheckCircleFillIcon, CircleIcon} from '@primer/octicons-react'
import {Radio} from '@primer/react'
import {testIdProps} from '@github-ui/test-id-props'
import type React from 'react'
import {type ChangeEventHandler, type ComponentPropsWithoutRef, forwardRef, useId} from 'react'
import {Tooltip} from '@primer/react/next'
import styles from './ColorPicker.module.css'
import {clsx} from 'clsx'
import type {PolymorphicForwardRefComponent} from '@github-ui/react-polymorphic'

export interface ColorPickerProps {
  value: ColorName
  onChange: (value: ColorName) => void
  label: React.ReactNode
}

// The tooltip component wraps its first immediate child with aria-attributes, however we want the
// aria-labelledby to be applied to the input element. This component prevents aria attributes from
// being applied to the incorrect element
const LabelWithoutAriaLabel = forwardRef(
  ({children, 'aria-describedby': _describedBy, 'aria-labelledby': _labelledby, ...props}, forwardedRef) => {
    return (
      <label {...props} ref={forwardedRef}>
        {children}
      </label>
    )
  },
) as PolymorphicForwardRefComponent<'label', ComponentPropsWithoutRef<'label'>>

LabelWithoutAriaLabel.displayName = 'LabelWithoutAriaLabel'

export const ColorPicker = ({value, onChange, label}: ColorPickerProps) => {
  const name = useId()

  const handleChange: ChangeEventHandler<HTMLInputElement> = event => {
    const newValue = event.target.value
    if (isColorName(newValue)) onChange(newValue)
  }

  return (
    <fieldset className={styles.Box}>
      <legend className={styles.Text}>{label}</legend>
      <div className={styles.Box_1}>
        {colorNames.map(color => (
          <ColorOption key={color} name={name} color={color} onChange={handleChange} selected={value === color} />
        ))}
      </div>
    </fieldset>
  )
}

interface ColorOptionProps {
  name: string
  color: ColorName
  onChange: ChangeEventHandler<HTMLInputElement>
  selected: boolean
}

const ColorOption = ({color, onChange, selected, name}: ColorOptionProps) => {
  const {fg, bg} = useNamedColor(color)
  const capitalizedColor = (color[0] ?? '') + color.slice(1).toLowerCase()
  const id = useId()

  return (
    <Tooltip text={capitalizedColor} direction="s" id={id}>
      <LabelWithoutAriaLabel
        style={{
          '--color-picker-fg': fg,
          '--color-picker-bg': bg,
        }}
        className={clsx(styles.Label, selected && styles.isSelected)}
        {...testIdProps(`color-picker-option-${capitalizedColor}`)}
      >
        <Radio
          aria-labelledby={id}
          name={name}
          value={color}
          checked={selected}
          onChange={onChange}
          className={styles.Radio}
        />
        {selected ? <CheckCircleFillIcon /> : <CircleIcon />}
      </LabelWithoutAriaLabel>
    </Tooltip>
  )
}

try{ ColorPicker.displayName ||= 'ColorPicker' } catch {}
try{ ColorOption.displayName ||= 'ColorOption' } catch {}